import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Offcanvas,
  Button,
  Form,
  Table,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faListAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import api from '@services/api'; // Assurez-vous que le chemin est correct

const PartnerCategoriesOffcanvas = ({
  show,
  onHide,
  onCategoriesUpdate // Fonction pour rafraîchir la liste dans le composant parent
}) => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null); // { id, name } ou null
  const [categoryName, setCategoryName] = useState('');
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/admin/partner-categories');
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        toast(
          response.data.message ||
            'Erreur lors de la récupération des catégories.'
        );
      }
    } catch (err) {
      toast('Erreur réseau lors de la récupération des catégories.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchCategories();
      setEditingCategory(null);
      setCategoryName('');
      setError(null);
    }
  }, [show]);

  const handleInputChange = e => {
    setCategoryName(e.target.value);
    if (error) setError(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!categoryName.trim()) {
      setError({ name: ['Le nom de la catégorie est requis.'] });
      return;
    }
    setIsSubmitting(true);
    setError(null);
    const url = editingCategory
      ? `/admin/partner-categories/${editingCategory.id}`
      : '/admin/partner-categories';
    const method = editingCategory ? 'put' : 'post';

    try {
      const response = await api[method](url, { name: categoryName });
      if (response.data.success) {
        toast(response.data.message);
        setCategoryName('');
        setEditingCategory(null);
        fetchCategories(); // Re-fetch all categories
        if (onCategoriesUpdate) {
          onCategoriesUpdate(); // Callback pour mettre à jour le select dans le parent
        }
      } else {
        setError(response.data.errors || { _error: [response.data.message] });
        toast(response.data.message || 'Une erreur est survenue.');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.errors || { _error: ['Erreur réseau.'] });
      toast(err.response?.data?.message || 'Erreur réseau.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = category => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setError(null);
    window.scrollTo(0, 0); // Scroll to top of offcanvas for better UX
  };

  const handleDelete = async categoryId => {
    if (
      window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')
    ) {
      setIsLoading(true); // Use general loading or a specific deleting state
      try {
        const response = await api.delete(
          `/admin/partner-categories/${categoryId}`
        );
        if (response.data.success) {
          toast(response.data.message);
          fetchCategories();
          if (onCategoriesUpdate) {
            onCategoriesUpdate();
          }
          if (editingCategory && editingCategory.id === categoryId) {
            setEditingCategory(null);
            setCategoryName('');
          }
        } else {
          toast(response.data.message || 'Erreur lors de la suppression.');
        }
      } catch (err) {
        toast(
          err.response?.data?.message || 'Erreur réseau lors de la suppression.'
        );
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const clearForm = () => {
    setEditingCategory(null);
    setCategoryName('');
    setError(null);
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: '500px' }}
    >
      <Offcanvas.Header closeButton className="bg-light">
        <Offcanvas.Title>
          <FontAwesomeIcon icon={faListAlt} className="me-2" />
          Gérer les catégories de partenaires
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form
          onSubmit={handleSubmit}
          className="mb-4 p-3 border rounded bg-light"
        >
          <h5>
            {editingCategory
              ? 'Modifier la catégorie'
              : 'Ajouter une catégorie'}
          </h5>
          <Form.Group className="mb-3" controlId="categoryName">
            <Form.Label>Nom de la catégorie</Form.Label>
            <Form.Control
              type="text"
              value={categoryName}
              onChange={handleInputChange}
              placeholder="Ex: Grands comptes"
              isInvalid={!!error?.name}
              disabled={isSubmitting}
            />
            {error?.name && (
              <Form.Control.Feedback type="invalid">
                {error.name.join(', ')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          {error?._error && (
            <div className="text-danger mb-2 small">
              {error._error.join(', ')}
            </div>
          )}
          <Row>
            <Col>
              {editingCategory && (
                <Button
                  variant="secondary"
                  onClick={clearForm}
                  className="me-2"
                  disabled={isSubmitting}
                >
                  Annuler l'édition
                </Button>
              )}
            </Col>
            <Col xs="auto">
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={editingCategory ? faEdit : faPlus}
                    className="me-1"
                  />
                )}
                {editingCategory ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </Col>
          </Row>
        </Form>

        <h5>Catégories existantes</h5>
        {isLoading && !categories.length ? (
          <div className="text-center py-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </div>
        ) : categories.length > 0 ? (
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Nom</th>
                <th width="120" className="text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id}>
                  <td>{cat.name}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleEdit(cat)}
                      title="Modifier"
                      disabled={isSubmitting || isLoading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(cat.id)}
                      title="Supprimer"
                      disabled={isSubmitting || isLoading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted mt-3">Aucune catégorie trouvée.</p>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

PartnerCategoriesOffcanvas.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onCategoriesUpdate: PropTypes.func.isRequired
};

export default PartnerCategoriesOffcanvas;
